import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type DataFreshness = 'fresh' | 'aging' | 'stale' | 'unavailable' | string;

const LABELS: Record<string, string> = {
  fresh: 'FRESH',
  aging: 'AGING',
  stale: 'STALE',
  unavailable: 'UNVERIFIED',
};

const DESCRIPTIONS: Record<string, string> = {
  fresh: 'Price verified recently.',
  aging: 'Price may be slightly out of date.',
  stale: 'Price has not been verified in a while — confirm at the retailer.',
  unavailable: 'Could not verify this price recently.',
};

function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * Surfaces a listing's data-freshness state and last-updated time.
 * Freshness must be visible wherever a listing is shown (CLAUDE.md cross-cutting
 * rule #2 / spec §7.2). Color reinforces — but does not solely convey — the label.
 */
export function FreshnessChip({
  freshness,
  lastUpdated,
  sx,
  testId,
}: {
  freshness: DataFreshness;
  lastUpdated?: string | null;
  sx?: React.ComponentProps<typeof Box>['sx'];
  /** Optional test hook (search/home tests query `freshness-chip`). */
  testId?: string;
}) {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const label = LABELS[freshness] ?? String(freshness).toUpperCase();
  const rel = relativeTime(lastUpdated);
  const color =
    freshness === 'fresh'
      ? t.green
      : freshness === 'aging'
        ? theme.palette.warning.main
        : freshness === 'stale'
          ? theme.palette.warning.dark
          : t.dim;

  const tip = `${DESCRIPTIONS[freshness] ?? 'Freshness unknown.'}${rel ? ` Updated ${rel}.` : ''}`;

  return (
    <Tooltip title={tip}>
      <Box
        component="span"
        data-testid={testId}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontFamily: t.monoFont,
          fontSize: 10,
          letterSpacing: '0.08em',
          color,
          ...sx,
        }}
      >
        <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color }} />
        {label}
        {rel && (
          <Box component="span" sx={{ color: t.dim }}>
            · {rel}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

export default FreshnessChip;
